import { useParams } from "react-router-dom";
import {
  getBasicFilteringOptions,
  getClearRequestType,
} from "../../common/EntitiesManagement";
import useGlobalContext from "../../hooks/useGlobalContext";
import { useEffect, useState } from "react";
import AddOrEditBasicEntity from "../../components/common/AddOrEditBasicEntity";
import {
  Alert,
  Box,
  Card,
  CardContent,
  TextField,
  Tooltip,
} from "@mui/material";
import localize from "../../common/localize";
import BackgroundForegroundColorPicker from "../../components/common/BackgroundForegroundColorPicker";
import ErrorMessage from "../../components/common/ErrorMessage";
import ModalBackground from "../../components/common/ModalBackground";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import { mutate } from "swr";
import { getInputLabelProps, getInputProps } from "../../common/appStyles";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function RequestTypes() {
  const {
    requestTypeModel,
    setRequestTypeModel,
    requestTypeData,
    REQUEST_TYPE_ENDPOINT,
    config,
    pageIndexRequestType,
    setPageIndexRequestType,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    basicFilterData,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [requestTypeDataFiltered, setRequestTypeDataFiltered] = useState(
    requestTypeData.Data
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getBasicFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const requestTypeDataObj = requestTypeData.Data.find(
        (requestTypeDataObj) => requestTypeDataObj.Id === Number(id)
      );
      setRequestTypeModel(requestTypeDataObj);
    }
  }, [id]);

  useEffect(() => {
    setRequestTypeDataFiltered(requestTypeData.Data);
  }, [requestTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setRequestTypeModel(getClearRequestType());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([REQUEST_TYPE_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndexRequestType !== 0 && requestTypeData.Data.length === 1) {
        setPageIndexRequestType(pageIndexRequestType - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${REQUEST_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${REQUEST_TYPE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${REQUEST_TYPE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleColorChange = (e) => {
    setRequestTypeModel({
      ...requestTypeModel,
      [e.target.name]: e.target.value,
    });
  };

  const getComponent = () => {
    return (
      <AddOrEditBasicEntity
        nameMandatory
        customWidth="100%"
        model={requestTypeModel}
        setModel={setRequestTypeModel}
        customComponent={
          <Box className="flex flex-col gap-2 w-full">
            <Box className="flex gap-2 w-full my-2">
              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={requestTypeModel.DaysUntilWarning}
                onChange={(e) => {
                  setRequestTypeModel({
                    ...requestTypeModel,
                    DaysUntilWarning: e.target.value > 0 ? e.target.value : 1,
                  });
                }}
                size="small"
                id="DaysUntilWarning"
                name="DaysUntilWarning"
                variant="outlined"
                label={localize.days_until_warning}
                fullWidth
              />

              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={requestTypeModel.DaysUntilError}
                onChange={(e) => {
                  setRequestTypeModel({
                    ...requestTypeModel,
                    DaysUntilError: e.target.value > 0 ? e.target.value : 1,
                  });
                }}
                size="small"
                id="DaysUntilError"
                name="DaysUntilError"
                variant="outlined"
                label={localize.days_until_error}
                fullWidth
              />
            </Box>

            <Box className="flex flex-col gap-2 w-full">
              <Alert severity="info" className="w-full">
                <label className="text-xs">
                  {localize.color_explanation.replace(
                    "%1s",
                    localize.request_types_complement
                  )}
                </label>
              </Alert>

              <BackgroundForegroundColorPicker
                backgroundValue={requestTypeModel.BackgroundColor}
                foregroundValue={requestTypeModel.ForegroundColor}
                onChange={handleColorChange}
              />
            </Box>
          </Box>
        }
      />
    );
  };

  const disableSaveChanges = () =>
    !requestTypeModel?.Name ||
    requestTypeModel?.Name.replaceAll(" ", "").length <= 0;

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.description,
      localize.background_color,
      localize.foreground_color,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.request_types}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={REQUEST_TYPE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="request_type"
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_request_type}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(requestTypeModel);
            setShowEditModal(false);
            setRequestTypeModel(getClearRequestType());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setRequestTypeModel(getClearRequestType());
          }}
          Component={getComponent()}
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.request_types}
            subtitle={localize.request_types_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="request_type"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_request_type}
                onClickToSubmit={() => {
                  handleCreateRequest(requestTypeModel);
                  setShowAddModal(false);
                  setRequestTypeModel(getClearRequestType());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setRequestTypeModel(getClearRequestType());
                }}
                Component={getComponent()}
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={requestTypeModel.Name}
          entityType={localize.request_type}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setRequestTypeModel(getClearRequestType());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(requestTypeModel.Id);
            setShowDeleteModal(false);
            setRequestTypeModel(getClearRequestType());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexRequestType}
            setPageIndex={setPageIndexRequestType}
            dataArray={requestTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          requestTypeData.Data?.length !== 0
            ? requestTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setRequestTypeModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setRequestTypeModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <ValueOrNoDataLabel data={item.Description} />,
                  <Tooltip title={item.BackgroundColor}>
                    <button
                      className="btn btn-sm background_color_representation"
                      style={{
                        backgroundColor: `${item.BackgroundColor}`,
                      }}
                    />
                  </Tooltip>,
                  <Tooltip title={item.ForegroundColor}>
                    <p
                      className="text-2xl foreground_color_representation"
                      style={{ color: `${item.ForegroundColor}` }}
                    >
                      Aa
                    </p>
                  </Tooltip>,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
